import * as React from 'react';
import { slot, useId, useMergedRefs } from '@fluentui/react-components';
import { useAnnounce_unstable as useAnnounceContext } from '@fluentui/react-shared-contexts';
import { useFirstMount, useTimeout } from '@fluentui/react-utilities';
import { useOutputCard_unstable } from '@fluentai/output-card';
import { useMessageAttributes } from '../../utils/useMessageAttributes';
/**
 * Create the state required to render CopilotMessage.
 *
 * The returned state can be modified with hooks such as useCopilotMessageStyles_unstable,
 * before being passed to renderCopilotMessage_unstable.
 *
 * @param props - props from this instance of CopilotMessage
 * @param ref - reference to root HTMLElement of CopilotMessage
 */
export const useCopilotMessage_unstable = (props, ref) => {
  const {
    announcement,
    accessibleHeading,
    isLoading
  } = props;
  const cardState = useOutputCard_unstable(props, ref);
  const id = useId('copilot-message-', props.id);
  const rootRef = React.useRef(null);
  const {
    announce
  } = useAnnounceContext();
  // timeout for announcing "loading" at 10s intervals
  const [setLoadingAnnounceTimeout, clearLoadingAnnounceTimeout] = useTimeout();
  // track whether the message has loading; this should only change once from false -> true
  const hasLoaded = React.useRef(!isLoading);
  // if the message is loading when first mounted, kick off loading announcements
  const isFirstMount = useFirstMount();
  if (isFirstMount && !announcement && isLoading) {
    const announceLoading = () => {
      announce('loading...', {
        batchId: id
      });
      setLoadingAnnounceTimeout(() => {
        announceLoading();
      }, 10000);
    };
    announceLoading();
  }
  // announce the announcement message every time the prop changes
  React.useEffect(() => {
    if (announcement) {
      clearLoadingAnnounceTimeout();
      announce(announcement, {
        batchId: id
      });
    }
  }, [announce, announcement, clearLoadingAnnounceTimeout, id]);
  // automatically announce when loading is complete, if announcement is not defined
  React.useEffect(() => {
    if (!hasLoaded.current && !isLoading) {
      // flag message as having finished loading, and stop loading announcements
      hasLoaded.current = true;
      clearLoadingAnnounceTimeout();
      if (announcement === undefined) {
        var _rootRef_current;
        const messageText = (_rootRef_current = rootRef.current) === null || _rootRef_current === void 0 ? void 0 : _rootRef_current.textContent;
        // clear any queued batched announcements and announce the full message
        announce('', {
          batchId: id
        });
        messageText && announce(messageText);
      }
    }
  }, [announce, announcement, clearLoadingAnnounceTimeout, id, isLoading]);
  const accessibleProps = {
    id,
    'aria-labelledby': `${id}`
  };
  return {
    ...cardState,
    components: {
      ...cardState.components,
      accessibleHeading: 'h6'
    },
    root: {
      ...cardState.root,
      ...useMessageAttributes({
        id,
        defaultFocused: props.defaultFocused
      }),
      ...accessibleProps,
      ref: useMergedRefs(rootRef, cardState.root.ref)
    },
    accessibleHeading: slot.optional(accessibleHeading, {
      defaultProps: {
        children: 'Copilot said: '
      },
      elementType: 'h6',
      renderByDefault: true
    })
  };
};
//# sourceMappingURL=useCopilotMessage.js.map